import { Component, OnInit, AfterViewInit, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { AuthService } from '@shared/auth/auth.service';
import { SharedService } from '@shared/services/shared.service';
import { environment } from "@env/environment";
import { PreloaderService } from './services/core/preloader.service';
import { Subject, filter, takeUntil } from 'rxjs';
import { AccountService } from './services/account/account.service';

@Component({
  selector: 'app-root',
  template: '<router-outlet></router-outlet>',
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {

  defaultRoute: string = 'home';
  loggingIn: boolean = false;
  isIframe: boolean = false;
  private readonly _destroying$ = new Subject<void>();

  constructor(
    private preloader: PreloaderService,
    private router: Router,
    public sharedService: SharedService,
    public authService: AuthService,
    public dialog: MatDialog,
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,

    private accountService: AccountService
  ) {}

  redirectFromProfile: string = 'redirectFromProfile';

  userAuthenticated: boolean = false;

  ngOnInit() {
    this.isIframe = window !== window.parent && !window.opener;
    
    //redirect to https for non-development modes
    if (environment.build != "Development" && environment.enableHttpsRedirect) {
      if (location.protocol === 'http:') {
        window.location.href = location.href.replace('http', 'https');
      }
    }

    // Handle MSAL redirect response
    this.msalService.handleRedirectObservable()
      .pipe(takeUntil(this._destroying$))
      .subscribe({
        next: (result) => {
          if (result) {
            // Authentication successful after redirect
            this.msalService.instance.setActiveAccount(result.account);
            // console.log('Authentication successful after redirect:', result);
            this.authService.loadUserInformation();

            // Navigate to intended route or default
            if (environment.authEntireSite && this.authService.isAuthenticated()) {
              this.router.navigate(['/lab-user-menu']);
            }
          }
        },
        error: (error) => {
          console.error('Error during redirect handling:', error);
        }
      });

    // Listen for interaction status changes
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.checkAndSetActiveAccount();
      });

    // Listen for login success events
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        // console.log('Login success event received:', result);
        const payload = result.payload as any;
        if (payload && payload.account) {
          this.msalService.instance.setActiveAccount(payload.account);
          this.authService.loadUserInformation();
          this.accountService.logLogin().subscribe();
          // console.log('User information loaded after login success2:', this.authService.user);
        }
      });
  }

  ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
  }

  private checkAndSetActiveAccount(): void {
    let activeAccount = this.msalService.instance.getActiveAccount();

    if (!activeAccount && this.msalService.instance.getAllAccounts().length > 0) {
      let accounts = this.msalService.instance.getAllAccounts();
      this.msalService.instance.setActiveAccount(accounts[0]);
    }
  }

  ngAfterViewInit() {
    this.preloader.hide();
  }

  public authEntireSite() : boolean {
    return environment.authEntireSite;
  }

  public authorized(roles?: string[]): boolean {
    return this.authService.isAuthorized()
  }

  public processing(): boolean {
    return this.authService.isProcessing();
  }

  public homePage(): boolean {
    let page = !this.authService.isProcessing() && 
    ((this.authEntireSite() && this.authorized()) || 
    (!this.authEntireSite()));
    return page;
  }

  public errorPage(): boolean {
    let page = !this.authService.isProcessing() && this.authEntireSite() && !this.authorized();
    return page;
  }
}
